import { useMutation, UseMutationResult } from 'react-query';

import { defaultDashboardSettings } from 'src/utils/urls';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type ApiResponse<TData = unknown> = {
  Data?: TData;
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
};

type TResult = ApiResponse;

type TError = Error;

type TVariables = {
  id: string | null;
};

async function saveUserSettings({ id }: TVariables): Promise<TResult> {
  const response = await fetch(defaultDashboardSettings(id), {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      tenant: process.env.REACT_APP_TENANT_ID || '',
    },
    body: JSON.stringify({
      'Dashboard-Settings': 'Clinical call resolution',
    }),
  });

  let result: TResult | null = null;

  try {
    const text = await response.text();
    result = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!result) {
    throw new Error('Invalid server response');
  }

  return result;
}

function useSetDefaultDashbordSettings(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  unknown
> {
  return useMutation(async (params: TVariables) => {
    const result = await saveUserSettings({
      id: params.id,
    });

    if (!result.IsSuccess) {
      const errorMessage =
        result.Errors?.map(err => err.Message).join(', ') ||
        'Failed to reset dashboard settings';

      throw new Error(errorMessage);
    }

    return result;
  });
}

export { useSetDefaultDashbordSettings };
